/**
 * Types for payload definition
 * @author Carlos López Rodríguez
 */

import { WaterIQUserType } from './msalTypes';
import {
	Address,
	DeviceSelectionSource,
	FileUploadStatus,
	ReadingReason,
	ReportErrorEntityType,
	ResourceSourceType,
	SourceType,
} from './types';

/**
 * Generic type for all update payloads
 */
interface PayloadToUpdate {
	auditDate: string;
}

/**
 *  Type for payload which will be sent to T&C acknowledgement api
 */
export interface AcceptTermsPayload extends PayloadToUpdate {
	action: 'ACCEPT';
}

/**
 *  Type for payload which will be sent to notification read api
 */
export interface ReadNotificationPayload extends PayloadToUpdate {
	action: 'READ';
}

/**
 * Type for payload for linking user to client with code
 */
export interface LinkExternalIdentityPayload {
	code: string;
	userType?: string;
}

/**
 * Type for response of linking user to client with code
 */
export interface LinkExternalIdentityResponse {
	id: string;
	userType: WaterIQUserType;
}

interface LinkWithReferencesPayloadBase {
	clientReference: string;
}

interface LinkWithClientAndAuthReferencesPayload extends LinkWithReferencesPayloadBase {
	authorisationReference: string;
}

interface LinkWithClientAndMeterReferencesPayload extends LinkWithReferencesPayloadBase {
	meterReference: string;
}

interface UnlinkClientReferencePayload extends LinkWithReferencesPayloadBase {
	isUnlinkRequest: boolean;
	comment: string;
}

/**
 * Type for payload for linking user to client without code but with references.
 * Referecens have to include any of meter or authorisation
 */
export type LinkWithReferencesPayload =
	| LinkWithClientAndAuthReferencesPayload
	| LinkWithClientAndMeterReferencesPayload
	| UnlinkClientReferencePayload;

export interface DqpRegistrationPayload {
	CMIId: string;
	email: string;
	mobile: string;
}

/**
 * Type for payload which will be sent to create device reading
 */
export interface DeviceReadingPayload {
	date: string;
	rawReadingDials?: string;
	rawReadingVariable?: string;
	unitOfMeasure: string;
	accuracy?: string;
	location?: LatLongType;
	photoFileName: string;
	photoURI: string;
	readingSource?: string;
	selectionSource?: DeviceSelectionSource;
	sourceType?: ResourceSourceType;
	readingType: ReadingReason;
	clientDeclaration: boolean;
}

export interface LatLongType {
	latitude: number;
	longitude: number;
}

/**
 * Type for new report issue creation
 */
export interface IssueCreation {
	message: string;
	field?: string;
	entityType?: ReportErrorEntityType;
	entityId?: string | null;
}

export type FaultyDeviceRequestBody = {
	faultIdentifiedDate: string;
	faultReason: Array<FaultyDeviceRequestBody.faultReason>;
	relevantAuthorisations?: string;
	personDetails?: string;
	dqpName?: string;
	comment?: string;
	sourceType: SourceType;
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace FaultyDeviceRequestBody {
	export enum faultReason {
		UNREADABLE = 'Unreadable',
		NOT_RECORDING = 'NotRecordingWaterVolumeCorrectly',
		LEAKING_WATER = 'LeakingWaterOrAirFromTheDeviceOrItsAttachments',
		BROKEN_SEALS = 'BrokenOrDamagedTamperEvidentSeals',
		NOT_TRANSMITTING = 'NotTransmittingInformation',
	}
}

// physical address search payloads
export interface AddressSearchPayload {
	search: string;
}
export interface AddressParsePayload {
	address: string;
}

export interface AddressParseResponse {
	resultCount: number;
	results: Address[];
}

//verify email payloads
export interface VerifyEmailPayload {
	emailAddress: string;
}
export interface VerifyEmailCheckPayload {
	emailAddress: string;
	code: string;
}
export interface VerifyEmailResponse {
	responseCode: 'VERIFIED' | 'REQUESTED' | 'SUCCESS' | 'FAILURE';
}

export interface FileUploadLinkResponse {
	id: string;
	sasuri: string;
}

export interface FileUploadStatusResponse {
	status: FileUploadStatus;
	fileuri: string | null;
	contactid: string | null;
}

export interface FileRetrieveResponse {
	content: string;
}
